import { AppHeader } from "../../../components/app-header/AppHeader";
import { AppNavigation } from "../../../components/app-navigation/AppNavigation";
import { AppFooterLinks } from "../../../components/app-footer/AppFooterLinks";
import {
  Avatar,
  ColumnDefinition,
  Container,
  DataTable,
  Layout,
  Pagination,
  StatusTag,
} from "@certn/cascadia-react";
import { PageTitle } from "../../../components/page-title/PageTitle";
import { useTranslation } from "react-i18next";
import { BasicButton, Stack, Text } from "@certn/cascadia";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { useNavigate } from "react-router-dom";
import { components } from "@certn/api/generated-schemas/client-portal";
import { formatDate } from "@certn/utils";
import { queryParamsToApiParams } from "../../utils";
import { AdjudicationFilters } from "./components/AdjudicationFilters";
import { useClientPortalAPI } from "@certn/api";
import { RubricActionsDropdown } from "./components/RubricActionsDropdown";
import { AdjudicationBetaModal } from "./components/AdjudicationBetaModal";
import { useFlags } from "launchdarkly-react-client-sdk";
import { AdjudicationBetaBadge } from "../../../components/adjudication-beta/AdjudicationBetaBadge";

export function AdjudicationListViewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { params, setParam } = useQueryParams();
  const { data, isLoading, mutate } = useClientPortalAPI(
    "/api/internal/client-portal/adjudication-rubrics/",
    queryParamsToApiParams(params)
  );
  const { showClientAdjudicationBeta } = useFlags();
  const statusMap = {
    DRAFT: (
      <StatusTag statusInfoIcon={false} fill="background-accent-neutral-weak">
        {t("Draft")}
      </StatusTag>
    ),
    ACTIVE: (
      <StatusTag
        statusInfoIcon={false}
        fill="background-accent-green-weak"
        color="text-accent-green-strong"
      >
        {t("Active")}
      </StatusTag>
    ),
    DEACTIVATED: (
      <StatusTag statusInfoIcon={false} fill="background-accent-red-weak">
        {t("Deactivated")}
      </StatusTag>
    ),
  };

  const columns: ColumnDefinition<
    components["schemas"]["AdjudicationRubricsList"]
  >[] = [
    {
      key: "name",
      name: t("Name"),
      isRowHeader: true,
      testID: "name-column-header",
      width: "25%",
    },
    {
      key: "checks",
      name: t("Checks"),
      isRowHeader: true,
      testID: "checks-column-header",
      width: "25%",
      render(rubric: components["schemas"]["AdjudicationRubricsList"]) {
        return rubric.checks.map((check: string) => (
          <StatusTag
            key={check}
            statusInfoIcon={false}
            fill="background-accent-neutral-weak"
          >
            {check}
          </StatusTag>
        ));
      },
    },
    {
      key: "usage",
      name: t("Usage"),
      isRowHeader: true,
      testID: "packages-column-header",
      width: "10%",
      render({ usage }: { usage: number }) {
        return (
          <Text style="form/body-secondary" testID="packages">
            {usage}
          </Text>
        );
      },
    },
    {
      key: "status",
      name: t("Status"),
      isRowHeader: true,
      testID: "status-column-header",
      width: "10%",
      render(item: components["schemas"]["AdjudicationRubricsList"]) {
        const status = item.is_active ? "ACTIVE" : "DEACTIVATED";
        return statusMap[status as keyof typeof statusMap];
      },
    },
    {
      key: "modified",
      name: t("Last Modified"),
      isRowHeader: true,
      width: "10%",
      testID: "last-modified-column-header",
      render({ modified }: { modified: string }) {
        return (
          <Stack gap="space-0">
            <Text style="form/body-secondary">{formatDate(modified)}</Text>
          </Stack>
        );
      },
    },
    {
      key: "created_by",
      name: t("Requestor"),
      isRowHeader: true,
      width: "10%",
      testID: "requestor-column-header",
      render({
        created_by,
      }: {
        created_by: { id: string; auth0_full_name: string | null } | null;
      }) {
        return (
          <Avatar
            name={created_by?.auth0_full_name || ""}
            testID="adjudication-requestor-avatar"
          />
        );
      },
    },
    {
      key: "actions",
      isAction: true,
      width: "10%",
      render(rubric: components["schemas"]["AdjudicationRubricsList"]) {
        return (
          <RubricActionsDropdown
            id={rubric.id}
            rubricName={rubric.name}
            isActive={rubric.is_active}
            mutate={mutate}
          />
        );
      },
    },
  ];

  return (
    <Layout
      layout="narrow"
      header={<AppHeader />}
      navigation={<AppNavigation />}
      footer={<AppFooterLinks />}
    >
      <Container layout="full-width">
        <Stack orientation="horizontal" justify="space-between">
          <PageTitle betaBadge={<AdjudicationBetaBadge />}>
            {t("Adjudication")}
          </PageTitle>
          <BasicButton onPress={() => navigate("/adjudication/create")}>
            {t("New Scoring Rubric")}
          </BasicButton>
        </Stack>
        <AdjudicationFilters />
        <DataTable
          aria-label={t("Adjudication rubrics table")}
          columns={columns}
          rows={data?.results || []}
          isLoading={isLoading}
          skeletonRows={6}
          onRowAction={(id) => {
            navigate(`/adjudication/${id}`, {
              state: {
                last: { href: `${location.pathname}${location.search}` },
              },
            });
          }}
        />
        <Pagination
          setPage={(page) => setParam("page", page.toString())}
          setPageCount={(pageCount) =>
            setParam("page_size", pageCount.toString())
          }
          total={data?.count || 0} // add total rows here
          page={params.page ? Number(params.page) : 1}
          pageCount={params["page_size"] ? Number(params["page_size"]) : 25}
          isLoading={isLoading}
        />
      </Container>
      {showClientAdjudicationBeta && <AdjudicationBetaModal />}
    </Layout>
  );
}
